import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@Utils';

import { DownloadCsv } from '@Actions';
import { add_svg, icon_search } from '@Assets';
import { PageWrapper, SupplierAddModal, SupplierList } from '@Components';
import buildSupplierExportRows from '@Components/SupplierList/exportRows';
import { Input } from '@Components/ui-v2/input';
import { TrackedButton } from '@Components/ui-v2/tracked-button';
import { DataSupplier } from '@Models';

interface SupplierProps {
  suppliers: DataSupplier[];
  supplierUpdated: () => void;
  canManage: boolean;
}

const Supplier = ({ suppliers, supplierUpdated, canManage }: SupplierProps) => {
  const { t } = useTranslation();
  const [loading, setloading] = useState(false);
  const [supplierList, setSupplierList] = useState(suppliers);
  const [openAdd, setOpenAdd] = useState(false);
  const [search, setSearch] = useState('');
  useEffect(() => {
    setSupplierList(suppliers);
  }, [suppliers]);

  // Exports every supplier, not the filtered/paginated view: the point of the
  // button is to hand over the full list. The row shape comes from the same
  // per-flavor column layout the table renders (see buildSupplierExportRows),
  // so the CSV can't drift from what the user sees on screen.
  const handleExport = () => {
    new DownloadCsv(t('Supplier.ExportFilename')).execute(buildSupplierExportRows(suppliers, t));
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e?.target?.value;
    if (!value) {
      setSearch('');
      setSupplierList(suppliers);
      return;
    }
    setSearch(value);
    // GLN and display name only — `name` is an internal identifier, and in
    // Logyca it is a slug the user has never seen.
    const filter = suppliers.filter(
      (supplier) =>
        (supplier.gln ?? '').includes(value) ||
        supplier.display_name.toLocaleLowerCase().includes(value.toLocaleLowerCase()),
    );
    setSupplierList(filter);
  };

  return (
    <div className={cn(loading && 'pointer-events-none', 'h-full w-full')}>
      <PageWrapper
        title={t('Supplier.Title')}
        actions={
          <div className="flex gap-2">
            {/* Not gated on canManage: read-only users see the list, so they
                can export it too. */}
            <TrackedButton
              buttonName="EXPORT_SUPPLIERS"
              isSubmit={false}
              variant="brand-secondary"
              size="icon"
              disabled={!suppliers?.length}
              aria-label={t('Supplier.Export')}
              title={t('Supplier.Export')}
              onClick={handleExport}>
              <Download size={18} strokeWidth={1.75} />
            </TrackedButton>
            {canManage && (
              <TrackedButton buttonName="OPEN_SUPPLIER_WINDOW" isSubmit={false} onClick={() => setOpenAdd(true)}>
                <div className="flex">
                  <img src={add_svg} alt="add_user" className="w-4 text-white mr-2" />
                  {t('Supplier.Add')}
                </div>
              </TrackedButton>
            )}
          </div>
        }>
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <div className="flex md:flex-row mx-6 my-4 items-center flex-col">
            <span className="font-bold text-xl justify-start text-gray-700">
              {`${t('Supplier.All')} (${suppliers?.length})`}
            </span>
            <div className="md:ml-auto mr-4">
              <div className="relative">
                <Input
                  type="text"
                  id="email"
                  onChange={handleSearch}
                  value={search}
                  className="sm:w-32 md:w-[250px] text-[15px] pl-8"
                  placeholder={t('Supplier.Search')}
                />
                <div className="absolute inset-y-0 left-0 pl-2 flex items-center  pointer-events-none">
                  <img src={icon_search} alt="add_user" className="w-4 text-white mr-2" />
                </div>
              </div>
            </div>
          </div>

          <SupplierList suppliers={supplierList} canManage={canManage} onSupplierUpdated={supplierUpdated} />
          {canManage && (
            <SupplierAddModal
              isOpen={openAdd}
              onClose={() => setOpenAdd(false)}
              fetchSuppliers={supplierUpdated}
              setloading={setloading}
              setSearch={setSearch}
            />
          )}
        </div>
      </PageWrapper>
    </div>
  );
};
export default Supplier;
